import LabelTextInput from 'src/modules/shared/components/atoms/inputs/label-text-input';
import Loading from 'src/modules/shared/components/atoms/loading';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { TemplateModalContextProps } from '../../../types/templates/template-composer.types';
import IntegrationPhoneSelect from '../phones/integration-phone-select';

/**
 * Fixed conversation context or aligned shared controls for standalone sends.
 * @param props - Current feature state and consumer-owned callbacks.
 * @returns The corresponding composer surface.
 */
export const TemplateModalContext = ({
  contextLocked,
  toNumber,
  selectedToNumber,
  isNumberValid,
  isNumberDebouncedValid,
  onNumberChange,
  phones,
  phonesLoading,
  selectedPhone,
  selectedPhoneId,
  onPhoneChange,
  autoSelectFirst,
}: TemplateModalContextProps) => {
  const { t } = useI18n();
  if (contextLocked) {
    return (
      <div className="flex shrink-0 flex-wrap items-baseline gap-x-5 gap-y-1 border-b border-secondary-100 pb-4">
        <Text size="small" color="secondary" className="min-w-0 break-words">
          {t('whatsappTemplates.to')}{' '}
          <span className="font-medium text-secondary-900">
            {selectedToNumber}
          </span>
        </Text>
        <Text size="small" color="secondary" className="min-w-0 break-words">
          {t('whatsappTemplates.from')}{' '}
          <span className="font-medium text-secondary-900">
            {selectedPhone?.displayName ??
              selectedPhone?.address ??
              t(
                'communications.messageBox.whatsapp.selectedAccountUnavailable'
              )}
          </span>
          {selectedPhone?.displayName && ` · ${selectedPhone.address}`}
        </Text>
        {!isNumberValid && (
          <Text size="tiny" className="w-full text-red-600">
            {t('whatsappTemplates.invalidNumber')}
          </Text>
        )}
      </div>
    );
  }
  return (
    <div className="grid min-w-0 shrink-0 items-start gap-5 border-b border-secondary-100 pb-5 pt-2 sm:grid-cols-2">
      {phonesLoading ? (
        <Loading />
      ) : (
        <IntegrationPhoneSelect
          phones={phones}
          selectedPhoneId={selectedPhoneId}
          onPhoneChange={onPhoneChange}
          autoSelectFirst={autoSelectFirst}
          labelPosition="top"
        />
      )}
      <LabelTextInput
        label={t('whatsappTemplates.to')}
        labelPosition="top"
        inputProps={{
          value: selectedToNumber,
          onChange: onNumberChange,
          readOnly: Boolean(toNumber),
          inputMode: 'tel',
          className: '!text-base',
          placeholder: '+56912345678',
          error:
            !isNumberValid &&
            selectedToNumber &&
            (Boolean(toNumber) || !isNumberDebouncedValid)
              ? t('whatsappTemplates.invalidNumber')
              : undefined,
        }}
      />
    </div>
  );
};
